import { useIntl } from 'react-intl';
import Select from '@rio-cloud/rio-uikit/Select';
import RadioButton from '@rio-cloud/rio-uikit/RadioButton';

import { NO_YES_OPTIONS, REPAIR_SCOPE_OPTIONS, SERVICE_CONDITION_OPTIONS, SERVICE_TYPE_OPTIONS } from '../constants';

import { AegFormSectionProps, type AegAttendanceConditionEnum, AegRepairScopeEnum, type AegServiceTypeEnum } from '../../../../../../models/newAEG';
import { UptimeCurrencyInput } from '../../../../../shared/UptimeCurrencyInput';
import { getOptionLabel, toSelectOptions } from '../../../../../../utils/aeg';

const RepairSection = ({ values, errors, requiredFields, onChange }: AegFormSectionProps) => {
    const intl = useIntl();
    const requiredMessage = intl.formatMessage({ id: 'intl-msg:support.custom.fieldRequired' });
    const selectPlaceholder = intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.placeholder.select' });

    return (
        <section className='border rounded padding-15'>
            <h3 className='display-flex align-items-center gap-x-2 margin-top-0 margin-bottom-15 text-size-16 text-bold'>
                <span className='rounded-circle bg-primary text-color-white text-size-12 width-25 height-25 inline-flex items-center justify-center shrink-0'>
                    4
                </span>
                {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.section.repair' })}
            </h3>
            <div className='grid grid-cols-[minmax(0,1fr)] sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3'>
                <div className='form-group margin-bottom-0'>
                    <label htmlFor='aeg-service-order' className='text-size-12 text-medium text-color-dark'>
                        {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.field.serviceOrder' })}{' '}
                        {requiredFields.includes('serviceOrder') && <span className='text-color-danger'>*</span>}
                    </label>
                    <input
                        id='aeg-service-order'
                        type='text'
                        className='form-control'
                        value={values.serviceOrder}
                        onChange={event => onChange('serviceOrder', event.target.value)}
                        placeholder={intl.formatMessage({
                            id: 'intl-msg:support.aeg.newRequest.placeholder.serviceOrder',
                        })}
                    />
                    {errors.serviceOrder && <span className='text-size-12 text-color-danger'>{requiredMessage}</span>}
                </div>
                <div className='form-group margin-bottom-0'>
                    <label htmlFor='aeg-warranty-request' className='text-size-12 text-medium text-color-dark'>
                        {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.field.warrantyRequest' })}{' '}
                        {requiredFields.includes('warrantyRequest') && <span className='text-color-danger'>*</span>}
                    </label>
                    <input
                        id='aeg-warranty-request'
                        type='text'
                        className='form-control'
                        value={values.warrantyRequest}
                        onChange={event => onChange('warrantyRequest', event.target.value)}
                        placeholder={intl.formatMessage({
                            id: 'intl-msg:support.aeg.newRequest.placeholder.warrantyRequest',
                        })}
                    />
                    {errors.warrantyRequest && (
                        <span className='text-size-12 text-color-danger'>{requiredMessage}</span>
                    )}
                </div>
                <div className='form-group margin-bottom-0'>
                    <label htmlFor='aeg-service-type' className='text-size-12 text-medium text-color-dark'>
                        {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.field.serviceType' })}{' '}
                        {requiredFields.includes('serviceType') && <span className='text-color-danger'>*</span>}
                    </label>
                    <Select
                        id='aeg-service-type'
                        options={toSelectOptions(SERVICE_TYPE_OPTIONS, intl)}
                        value={values.serviceType ? [values.serviceType] : []}
                        onChange={option => onChange('serviceType', (option?.id ?? '') as AegServiceTypeEnum | '')}
                        placeholder={selectPlaceholder}
                    />
                    {errors.serviceType && <span className='text-size-12 text-color-danger'>{requiredMessage}</span>}
                </div>
                <div className='form-group margin-bottom-0'>
                    <label htmlFor='aeg-repair-total' className='text-size-12 text-medium text-color-dark'>
                        {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.field.repairTotal' })}{' '}
                        {requiredFields.includes('repairTotal') && <span className='text-color-danger'>*</span>}
                    </label>
                    <UptimeCurrencyInput
                        value={values.repairTotal}
                        onChange={value => onChange('repairTotal', value)}
                        placeholder={intl.formatMessage({
                            id: 'intl-msg:support.aeg.newRequest.placeholder.repairTotal',
                        })}
                    />
                    {errors.repairTotal && <span className='text-size-12 text-color-danger'>{requiredMessage}</span>}
                </div>
                <div className='form-group margin-bottom-0'>
                    <label htmlFor='aeg-labor-total' className='text-size-12 text-medium text-color-dark'>
                        {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.field.laborTotal' })}{' '}
                        {requiredFields.includes('laborTotal') && <span className='text-color-danger'>*</span>}
                    </label>
                    <input
                        id='aeg-labor-total'
                        type='text'
                        className='form-control'
                        value={values.laborTotal}
                        onChange={event => onChange('laborTotal', event.target.value)}
                        placeholder={intl.formatMessage({
                            id: 'intl-msg:support.aeg.newRequest.placeholder.laborTotal',
                        })}
                    />
                    {errors.laborTotal && <span className='text-size-12 text-color-danger'>{requiredMessage}</span>}
                </div>
                <div className='form-group margin-bottom-0'>
                    <label htmlFor='aeg-service-condition' className='text-size-12 text-medium text-color-dark'>
                        {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.field.serviceCondition' })}{' '}
                        {requiredFields.includes('serviceCondition') && <span className='text-color-danger'>*</span>}
                    </label>
                    <Select
                        id='aeg-service-condition'
                        options={toSelectOptions(SERVICE_CONDITION_OPTIONS, intl)}
                        value={values.serviceCondition ? [values.serviceCondition] : []}
                        onChange={option =>
                            onChange('serviceCondition', (option?.id ?? '') as AegAttendanceConditionEnum | '')
                        }
                        placeholder={selectPlaceholder}
                    />
                    {errors.serviceCondition && (
                        <span className='text-size-12 text-color-danger'>{requiredMessage}</span>
                    )}
                </div>
                <div
                    className='form-group margin-bottom-0'
                    role='radiogroup'
                    aria-labelledby='aeg-has-third-party-label'
                >
                    <span
                        id='aeg-has-third-party-label'
                        className='display-block text-size-12 text-medium text-color-dark'
                    >
                        {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.field.hasThirdParty' })}{' '}
                        {requiredFields.includes('hasThirdParty') && <span className='text-color-danger'>*</span>}
                    </span>
                    <div className='aeg-radio-group'>
                        {NO_YES_OPTIONS.map(option => (
                            <RadioButton
                                key={option.id}
                                name='aeg-has-third-party'
                                value={option.id}
                                checked={values.hasThirdParty === (option.id === 'YES')}
                                onChange={() => onChange('hasThirdParty', option.id === 'YES')}
                                inline
                            >
                                {getOptionLabel(option, intl)}
                            </RadioButton>
                        ))}
                    </div>
                    {errors.hasThirdParty && <span className='text-size-12 text-color-danger'>{requiredMessage}</span>}
                </div>
                {values.hasThirdParty && (
                    <>
                        <div className='form-group margin-bottom-0 border-l-4 border-color-primary padding-left-10'>
                            <label
                                htmlFor='aeg-third-party-services'
                                className='text-size-12 text-medium text-color-dark'
                            >
                                {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.field.thirdPartyServices' })}{' '}
                                {requiredFields.includes('thirdPartyServices') && (
                                    <span className='text-color-danger'>*</span>
                                )}
                            </label>
                            <input
                                id='aeg-third-party-services'
                                type='text'
                                className='form-control'
                                value={values.thirdPartyServices}
                                onChange={event => onChange('thirdPartyServices', event.target.value)}
                                placeholder={intl.formatMessage({
                                    id: 'intl-msg:support.aeg.newRequest.placeholder.thirdPartyServices',
                                })}
                            />
                            {errors.thirdPartyServices && (
                                <span className='text-size-12 text-color-danger'>{requiredMessage}</span>
                            )}
                        </div>
                        <div className='form-group margin-bottom-0 border-l-4 border-color-primary padding-left-10'>
                            <label
                                htmlFor='aeg-third-party-invoice-value'
                                className='text-size-12 text-medium text-color-dark'
                            >
                                {intl.formatMessage({
                                    id: 'intl-msg:support.aeg.newRequest.field.thirdPartyInvoiceValue',
                                })}{' '}
                                {requiredFields.includes('thirdPartyInvoiceValue') && (
                                    <span className='text-color-danger'>*</span>
                                )}
                            </label>
                            <UptimeCurrencyInput
                                value={values.thirdPartyInvoiceValue}
                                onChange={value => onChange('thirdPartyInvoiceValue', value)}
                                placeholder={intl.formatMessage({
                                    id: 'intl-msg:support.aeg.newRequest.placeholder.thirdPartyInvoiceValue',
                                })}
                            />
                            {errors.thirdPartyInvoiceValue && (
                                <span className='text-size-12 text-color-danger'>{requiredMessage}</span>
                            )}
                        </div>
                        <div className='form-group margin-bottom-0 border-l-4 border-color-primary padding-left-10'>
                            <label
                                htmlFor='aeg-third-party-invoice-number'
                                className='text-size-12 text-medium text-color-dark'
                            >
                                {intl.formatMessage({
                                    id: 'intl-msg:support.aeg.newRequest.field.thirdPartyInvoiceNumber',
                                })}{' '}
                                {requiredFields.includes('thirdPartyInvoiceNumber') && (
                                    <span className='text-color-danger'>*</span>
                                )}
                            </label>
                            <input
                                id='aeg-third-party-invoice-number'
                                type='text'
                                className='form-control'
                                value={values.thirdPartyInvoiceNumber}
                                onChange={event => onChange('thirdPartyInvoiceNumber', event.target.value)}
                                placeholder={intl.formatMessage({
                                    id: 'intl-msg:support.aeg.newRequest.placeholder.thirdPartyInvoiceNumber',
                                })}
                            />
                            {errors.thirdPartyInvoiceNumber && (
                                <span className='text-size-12 text-color-danger'>{requiredMessage}</span>
                            )}
                        </div>
                    </>
                )}
                <div className='form-group margin-bottom-0' role='radiogroup' aria-labelledby='aeg-repair-scope-label'>
                    <span
                        id='aeg-repair-scope-label'
                        className='display-block text-size-12 text-medium text-color-dark'
                    >
                        {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.field.repairScope' })}{' '}
                        {requiredFields.includes('repairScope') && <span className='text-color-danger'>*</span>}
                    </span>
                    <div className='aeg-radio-group'>
                        {REPAIR_SCOPE_OPTIONS.map(option => (
                            <RadioButton
                                key={option.id}
                                name='aeg-repair-scope'
                                value={option.id}
                                checked={values.repairScope === option.id}
                                onChange={() => onChange('repairScope', option.id as AegRepairScopeEnum)}
                                inline
                            >
                                {getOptionLabel(option, intl)}
                            </RadioButton>
                        ))}
                    </div>
                    {errors.repairScope && <span className='text-size-12 text-color-danger'>{requiredMessage}</span>}
                </div>
                {values.repairScope === AegRepairScopeEnum.PARTIAL && (
                    <div className='form-group margin-bottom-0 border-l-4 border-color-primary padding-left-10'>
                        <label htmlFor='aeg-repair-percentage' className='text-size-12 text-medium text-color-dark'>
                            {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.field.repairPercentage' })}{' '}
                            {requiredFields.includes('repairPercentage') && (
                                <span className='text-color-danger'>*</span>
                            )}
                        </label>
                        <input
                            id='aeg-repair-percentage'
                            type='text'
                            className='form-control'
                            value={values.repairPercentage}
                            onChange={event => onChange('repairPercentage', event.target.value)}
                            placeholder={intl.formatMessage({
                                id: 'intl-msg:support.aeg.newRequest.placeholder.repairPercentage',
                            })}
                        />
                        {errors.repairPercentage && (
                            <span className='text-size-12 text-color-danger'>{requiredMessage}</span>
                        )}
                    </div>
                )}
                <div className='form-group margin-bottom-0'>
                    <label htmlFor='aeg-control-code' className='text-size-12 text-medium text-color-dark'>
                        {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.field.controlCode' })}{' '}
                        {requiredFields.includes('controlCode') && <span className='text-color-danger'>*</span>}
                    </label>
                    <input
                        id='aeg-control-code'
                        type='text'
                        className='form-control'
                        value={values.controlCode}
                        onChange={event => onChange('controlCode', event.target.value)}
                        placeholder={intl.formatMessage({
                            id: 'intl-msg:support.aeg.newRequest.placeholder.controlCode',
                        })}
                    />
                    {errors.controlCode && <span className='text-size-12 text-color-danger'>{requiredMessage}</span>}
                </div>
                <div className='form-group margin-bottom-0'>
                    <label htmlFor='aeg-chame-volks' className='text-size-12 text-medium text-color-dark'>
                        {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.field.chameVolks' })}{' '}
                        {requiredFields.includes('chameVolks') && <span className='text-color-danger'>*</span>}
                    </label>
                    <input
                        id='aeg-chame-volks'
                        type='text'
                        className='form-control'
                        value={values.chameVolks}
                        onChange={event => onChange('chameVolks', event.target.value)}
                        placeholder={intl.formatMessage({
                            id: 'intl-msg:support.aeg.newRequest.placeholder.chameVolks',
                        })}
                    />
                    {errors.chameVolks && <span className='text-size-12 text-color-danger'>{requiredMessage}</span>}
                </div>
                <div className='form-group margin-bottom-0' role='radiogroup' aria-labelledby='aeg-breakdown-label'>
                    <span id='aeg-breakdown-label' className='display-block text-size-12 text-medium text-color-dark'>
                        {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.field.breakdown' })}{' '}
                        {requiredFields.includes('breakdown') && <span className='text-color-danger'>*</span>}
                    </span>
                    <div className='aeg-radio-group'>
                        {NO_YES_OPTIONS.map(option => (
                            <RadioButton
                                key={option.id}
                                name='aeg-breakdown'
                                value={option.id}
                                checked={values.breakdown === (option.id === 'YES')}
                                onChange={() => onChange('breakdown', option.id === 'YES')}
                                inline
                            >
                                {getOptionLabel(option, intl)}
                            </RadioButton>
                        ))}
                    </div>
                    {errors.breakdown && <span className='text-size-12 text-color-danger'>{requiredMessage}</span>}
                </div>
                <div className='form-group margin-bottom-0 col-span-full'>
                    <label htmlFor='aeg-fleet-composition' className='text-size-12 text-medium text-color-dark'>
                        {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.field.fleetComposition' })}{' '}
                        {requiredFields.includes('fleetComposition') && <span className='text-color-danger'>*</span>}
                    </label>
                    <textarea
                        id='aeg-fleet-composition'
                        className='form-control'
                        rows={3}
                        value={values.fleetComposition}
                        onChange={event => onChange('fleetComposition', event.target.value)}
                        placeholder={intl.formatMessage({
                            id: 'intl-msg:support.aeg.newRequest.placeholder.fleetComposition',
                        })}
                    />
                    {errors.fleetComposition && (
                        <span className='text-size-12 text-color-danger'>{requiredMessage}</span>
                    )}
                </div>
                <div className='form-group margin-bottom-0 col-span-full'>
                    <label htmlFor='aeg-companies' className='text-size-12 text-medium text-color-dark'>
                        {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.field.companies' })}{' '}
                        {requiredFields.includes('companies') && <span className='text-color-danger'>*</span>}
                    </label>
                    <textarea
                        id='aeg-companies'
                        className='form-control'
                        rows={3}
                        value={values.companies}
                        onChange={event => onChange('companies', event.target.value)}
                        placeholder={intl.formatMessage({
                            id: 'intl-msg:support.aeg.newRequest.placeholder.companies',
                        })}
                    />
                    {errors.companies && <span className='text-size-12 text-color-danger'>{requiredMessage}</span>}
                </div>
                <div className='form-group margin-bottom-0 col-span-full'>
                    <label htmlFor='aeg-customer-complaint' className='text-size-12 text-medium text-color-dark'>
                        {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.field.customerComplaint' })}{' '}
                        {requiredFields.includes('customerComplaint') && <span className='text-color-danger'>*</span>}
                    </label>
                    <textarea
                        id='aeg-customer-complaint'
                        className='form-control'
                        rows={3}
                        value={values.customerComplaint}
                        onChange={event => onChange('customerComplaint', event.target.value)}
                        placeholder={intl.formatMessage({
                            id: 'intl-msg:support.aeg.newRequest.placeholder.customerComplaint',
                        })}
                    />
                    {errors.customerComplaint && (
                        <span className='text-size-12 text-color-danger'>{requiredMessage}</span>
                    )}
                </div>
                <div className='form-group margin-bottom-0 col-span-full'>
                    <label htmlFor='aeg-dn-diagnosis' className='text-size-12 text-medium text-color-dark'>
                        {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.field.dnDiagnosis' })}{' '}
                        {requiredFields.includes('dnDiagnosis') && <span className='text-color-danger'>*</span>}
                    </label>
                    <textarea
                        id='aeg-dn-diagnosis'
                        className='form-control'
                        rows={3}
                        value={values.dnDiagnosis}
                        onChange={event => onChange('dnDiagnosis', event.target.value)}
                        placeholder={intl.formatMessage({
                            id: 'intl-msg:support.aeg.newRequest.placeholder.dnDiagnosis',
                        })}
                    />
                    {errors.dnDiagnosis && <span className='text-size-12 text-color-danger'>{requiredMessage}</span>}
                </div>
                <div className='form-group margin-bottom-0 col-span-full'>
                    <label htmlFor='aeg-dn-justification' className='text-size-12 text-medium text-color-dark'>
                        {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.field.dnJustification' })}{' '}
                        {requiredFields.includes('dnJustification') && <span className='text-color-danger'>*</span>}
                    </label>
                    <textarea
                        id='aeg-dn-justification'
                        className='form-control'
                        rows={3}
                        value={values.dnJustification}
                        onChange={event => onChange('dnJustification', event.target.value)}
                        placeholder={intl.formatMessage({
                            id: 'intl-msg:support.aeg.newRequest.placeholder.dnJustification',
                        })}
                    />
                    {errors.dnJustification && (
                        <span className='text-size-12 text-color-danger'>{requiredMessage}</span>
                    )}
                </div>
            </div>
        </section>
    );
};

export default RepairSection;
